import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiArrowRight, FiMapPin, FiBriefcase, FiMail, FiHeart, FiUser, FiPhone, FiFileText, FiUpload, FiCheck, FiX, FiSend } from 'react-icons/fi';
import api from '../api';

const C = {
  rose: '#e3425c', roseLight: 'rgba(227,66,92,0.08)', roseDeep: '#c94d5c',
  gold: '#d4a853', goldLight: 'rgba(212,168,83,0.12)', goldDark: '#b8923e',
  dark: '#1c1c1e', darkLight: '#3a3a3c', cream: '#f7f7f8', creamDark: '#ebebed',
  white: '#ffffff', gray: '#8e8e93', grayLight: '#aeaeb2',
};

const OPENINGS = [
  { title: 'Full-Stack Developer', type: 'Full-time', location: 'Remote (SA)', desc: 'Build features for our event platform using React, Node.js, and PostgreSQL. You\'ll work on everything from the vendor marketplace to real-time chat.' },
  { title: 'UI/UX Designer', type: 'Full-time / Contract', location: 'Remote (SA)', desc: 'Design beautiful, intuitive interfaces for hosts, guests, and vendors. Strong portfolio with mobile-first responsive design required.' },
  { title: 'Marketing & Growth Lead', type: 'Full-time', location: 'Johannesburg / Remote', desc: 'Drive user acquisition, vendor onboarding, and brand awareness across South Africa. Experience in digital marketing and SA event industry is a plus.' },
  { title: 'Customer Success Associate', type: 'Part-time', location: 'Remote (SA)', desc: 'Help hosts and vendors get the most out of Grand Gather. Handle enquiries, onboarding, and build relationships with our community.' },
  { title: 'Vendor Partnerships Manager', type: 'Full-time', location: 'Gauteng / Remote', desc: 'Source, vet, and onboard top event vendors across SA. Build partnerships with caterers, photographers, DJs, venue owners and more.' },
];

export default function Careers() {
  const [applyJob, setApplyJob] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', coverLetter: '', portfolio: '' });
  const [cvFile, setCvFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleApply = (jobTitle) => {
    setApplyJob(jobTitle);
    setForm({ name: '', email: '', phone: '', coverLetter: '', portfolio: '' });
    setCvFile(null);
    setSubmitted(false);
    setError('');
    setTimeout(() => {
      document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { setError('Please fill in your name, email, and phone number.'); return; }
    setError('');
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('email', form.email);
      formData.append('phone', form.phone);
      formData.append('position', applyJob);
      formData.append('coverLetter', form.coverLetter);
      formData.append('portfolio', form.portfolio);
      if (cvFile) formData.append('cv', cvFile);
      await api.post('/careers/apply', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
    }
    setLoading(false);
  };

  const inputStyle = {
    width: '100%', padding: '13px 14px', borderRadius: '8px',
    border: `1px solid ${C.creamDark}`, fontSize: '14px',
    fontFamily: 'Inter, -apple-system, sans-serif',
    color: C.dark, background: C.white, outline: 'none',
    boxSizing: 'border-box', transition: 'border-color 0.2s',
  };

  return (
    <div style={{ minHeight: '100vh', background: C.cream, fontFamily: 'Inter, -apple-system, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        .career-input:focus { border-color: ${C.rose} !important; box-shadow: 0 0 0 3px ${C.roseLight}; }
      `}</style>

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)', borderBottom: `1px solid ${C.creamDark}`, padding: '14px 0' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to='/' style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: C.dark, fontSize: '14px', fontWeight: 500 }}>
            <FiArrowLeft size={16} />
            <img src="/ggr-logo-deck.png" alt='GGR' style={{ height: '50px', width: 'auto' }} />
          </Link>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Link to='/about' style={{ padding: '9px 16px', borderRadius: '8px', color: C.dark, textDecoration: 'none', fontSize: '13px', fontWeight: 500 }}>About</Link>
            <Link to='/contact' style={{ padding: '9px 16px', borderRadius: '8px', color: C.dark, textDecoration: 'none', fontSize: '13px', fontWeight: 500 }}>Contact</Link>
          </div>
        </div>
      </nav>

      {/* Hero Banner */}
      <section style={{ background: `linear-gradient(135deg, ${C.dark} 0%, #2a2a2e 100%)`, padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '-20%', right: '-5%', width: '500px', height: '500px', borderRadius: '50%', background: `${C.rose}08` }} />
          <div style={{ position: 'absolute', bottom: '-10%', left: '10%', width: '300px', height: '300px', borderRadius: '50%', background: `${C.gold}06` }} />
        </div>
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '0 28px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 16px', borderRadius: '20px', background: `${C.rose}20`, color: C.rose, fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '20px' }}>
            <FiBriefcase size={12} /> Careers
          </div>
          <h1 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: 'clamp(34px, 5vw, 52px)', fontWeight: 700, color: C.white, lineHeight: 1.15, marginBottom: '16px' }}>
            Join the <span style={{ color: C.rose }}>celebration</span>
          </h1>
          <p style={{ fontSize: '17px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, maxWidth: '500px', margin: '0 auto' }}>
            We're building the future of event planning in South Africa. Come help us make every celebration unforgettable.
          </p>
        </div>
      </section>

      {/* Perks */}
      <section style={{ padding: '0 0 60px', background: C.cream }}>
        <div style={{ maxWidth: '900px', margin: '-32px auto 0', padding: '0 28px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', position: 'relative', zIndex: 2 }}>
          {[
            { icon: '🏠', text: 'Remote-first culture', color: C.rose },
            { icon: '💡', text: 'Build real products', color: C.gold },
            { icon: '🌍', text: 'Proudly South African', color: C.rose },
            { icon: '🚀', text: 'Fast-growing startup', color: C.gold },
          ].map((p, i) => (
            <div key={i} style={{ padding: '20px', background: C.white, borderRadius: '14px', border: `1px solid ${C.creamDark}`, textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>{p.icon}</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: C.dark }}>{p.text}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Open Positions */}
      <section style={{ padding: '40px 0 60px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 28px' }}>
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '28px', marginBottom: '6px', color: C.dark }}>Open Positions</h2>
            <p style={{ fontSize: '14px', color: C.gray }}>{OPENINGS.length} roles available — click Apply to fill in the form below</p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {OPENINGS.map((job, i) => (
              <div key={i} style={{
                padding: '28px', background: C.white, borderRadius: '16px',
                border: applyJob === job.title ? `2px solid ${C.rose}` : `1px solid ${C.creamDark}`,
                transition: 'all 0.2s', boxShadow: applyJob === job.title ? `0 4px 16px ${C.roseLight}` : '0 2px 8px rgba(0,0,0,0.02)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 700, color: C.dark, margin: 0 }}>{job.title}</h3>
                  <span style={{ padding: '4px 12px', borderRadius: '8px', background: `${C.rose}10`, color: C.rose, fontSize: '12px', fontWeight: 600 }}>{job.type}</span>
                </div>
                <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', color: C.gray, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FiMapPin size={12} /> {job.location}
                  </span>
                </div>
                <p style={{ fontSize: '14px', color: '#666', lineHeight: 1.7, marginBottom: '16px' }}>{job.desc}</p>
                <button onClick={() => handleApply(job.title)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 20px',
                  borderRadius: '8px',
                  background: applyJob === job.title ? C.rose : C.dark,
                  color: C.white, border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif', transition: 'all 0.2s',
                }}>
                  {applyJob === job.title ? <><FiCheck size={14} /> Selected</> : <><FiArrowRight size={14} /> Apply Now</>}
                </button>
              </div>
            ))}
          </div>

          {/* General application */}
          <div style={{ marginTop: '24px', padding: '32px', background: C.roseLight, borderRadius: '16px', textAlign: 'center', border: `1px solid rgba(227,66,92,0.1)` }}>
            <FiHeart size={28} style={{ color: C.rose, marginBottom: '12px' }} />
            <h3 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '20px', fontWeight: 700, marginBottom: '8px', color: C.dark }}>Don't see your role?</h3>
            <p style={{ fontSize: '14px', color: C.gray, lineHeight: 1.7, maxWidth: '400px', margin: '0 auto 20px' }}>
              We're always looking for talented people. Send us your CV and tell us how you'd like to contribute.
            </p>
            <button onClick={() => handleApply('General Application')} style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '12px 24px',
              borderRadius: '10px', background: C.rose, color: C.white, border: 'none',
              fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
              boxShadow: `0 4px 12px ${C.rose}30`,
            }}>
              <FiMail size={16} /> Apply Now
            </button>
          </div>
        </div>
      </section>

      {/* Application Form */}
      {applyJob && (
        <section id='application-form' style={{ padding: '0 0 80px' }}>
          <div style={{ maxWidth: '620px', margin: '0 auto', padding: '0 28px' }}>
            <div style={{ background: C.white, borderRadius: '20px', padding: '36px', border: `1px solid ${C.creamDark}`, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div>
                  <h3 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '22px', color: C.dark, margin: 0 }}>Apply for Position</h3>
                  <div style={{ fontSize: '14px', color: C.rose, fontWeight: 600, marginTop: '4px' }}>{applyJob}</div>
                </div>
                <button onClick={() => setApplyJob(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.gray, padding: '8px', fontSize: '20px' }}><FiX /></button>
              </div>

              {submitted ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <div style={{ width: '70px', height: '70px', borderRadius: '50%', background: `${C.gold}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <FiCheck size={32} style={{ color: C.gold }} />
                  </div>
                  <h4 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '22px', marginBottom: '8px', color: C.dark }}>Application Sent!</h4>
                  <p style={{ color: C.gray, fontSize: '15px', lineHeight: 1.6, maxWidth: '360px', margin: '0 auto 8px' }}>
                    Thank you for your interest in the <strong>{applyJob}</strong> position. Our team will review your application and get back to you.
                  </p>
                  <p style={{ color: C.grayLight, fontSize: '13px' }}>We typically respond within 3–5 business days.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '14px' }}>
                  {error && <div style={{ padding: '12px', background: `${C.red}10`, borderRadius: '8px', color: '#ef4444', fontSize: '14px' }}>{error}</div>}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: C.dark, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FiUser size={12} /> Full Name <span style={{ color: C.rose }}>*</span>
                      </label>
                      <input className='career-input' placeholder='Your full name' value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required style={inputStyle} />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: C.dark, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FiMail size={12} /> Email <span style={{ color: C.rose }}>*</span>
                      </label>
                      <input className='career-input' placeholder='your@email.com' type='email' value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required style={inputStyle} />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: C.dark, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FiPhone size={12} /> Phone <span style={{ color: C.rose }}>*</span>
                      </label>
                      <input className='career-input' placeholder='e.g., +27 61 391 1942' type='tel' value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} required style={inputStyle} />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: C.dark, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FiFileText size={12} /> Portfolio Link
                      </label>
                      <input className='career-input' placeholder='LinkedIn, GitHub, Behance...' value={form.portfolio} onChange={e => setForm({ ...form, portfolio: e.target.value })} style={inputStyle} />
                    </div>
                  </div>

                  {/* CV Upload */}
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: C.dark, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <FiUpload size={12} /> Upload CV / Resume <span style={{ color: C.rose }}>*</span>
                    </label>
                    <div onClick={() => fileRef.current?.click()} style={{
                      padding: '20px', borderRadius: '10px',
                      border: `2px dashed ${cvFile ? C.gold : C.creamDark}`,
                      background: cvFile ? `${C.gold}08` : C.cream, textAlign: 'center', cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}>
                      <input ref={fileRef} type='file' accept='.pdf,.doc,.docx' style={{ display: 'none' }}
                        onChange={e => { if (e.target.files[0]) setCvFile(e.target.files[0]); }} />
                      {cvFile ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                          <FiCheck size={16} style={{ color: C.gold }} />
                          <span style={{ fontSize: '14px', fontWeight: 600, color: C.gold }}>{cvFile.name}</span>
                          <button type='button' onClick={(e) => { e.stopPropagation(); setCvFile(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.gray, padding: '2px' }}><FiX size={14} /></button>
                        </div>
                      ) : (
                        <div>
                          <FiUpload size={24} style={{ color: C.grayLight, marginBottom: '8px' }} />
                          <p style={{ fontSize: '14px', color: C.gray, margin: 0 }}>Click to upload your CV</p>
                          <p style={{ fontSize: '12px', color: C.grayLight, margin: '4px 0 0' }}>PDF, DOC, or DOCX (max 5MB)</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: C.dark, marginBottom: '6px', display: 'block' }}>
                      Cover Letter / Why Grand Gather?
                    </label>
                    <textarea className='career-input' placeholder="Tell us why you'd be a great fit for Grand Gather and what excites you about this role..." rows={5} value={form.coverLetter}
                      onChange={e => setForm({ ...form, coverLetter: e.target.value })} style={{ ...inputStyle, resize: 'vertical' }} />
                  </div>

                  <button type='submit' disabled={loading} style={{
                    padding: '14px', borderRadius: '10px', border: 'none',
                    background: C.rose, color: C.white, fontSize: '15px', fontWeight: 600,
                    cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    opacity: loading ? 0.6 : 1, marginTop: '4px',
                    boxShadow: `0 4px 12px ${C.rose}30`,
                  }}>
                    <FiSend size={16} /> {loading ? 'Submitting...' : 'Submit Application'}
                  </button>
                  <p style={{ fontSize: '12px', color: C.grayLight, textAlign: 'center', margin: 0 }}>
                    Your application will be sent to careers@grandgatherregistry.co.za
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer style={{ padding: '24px 0', borderTop: `1px solid ${C.creamDark}`, textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '10px' }}>
          <Link to='/' style={{ fontSize: '13px', color: C.gray, textDecoration: 'none' }}>Home</Link>
          <Link to='/about' style={{ fontSize: '13px', color: C.gray, textDecoration: 'none' }}>About</Link>
          <Link to='/contact' style={{ fontSize: '13px', color: C.gray, textDecoration: 'none' }}>Contact</Link>
        </div>
        <p style={{ fontSize: '12px', color: C.grayLight }}>Made with love in South Africa &middot; &copy; 2026 Grand Gather Registry</p>
      </footer>
    </div>
  );
}